import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Loader2, Users, ListChecks, Mail, Copy, X, DollarSign, Contact2, Beaker } from "lucide-react";
import { PlatformBilling } from "@/components/BillingManager";
import { ProspectsPanel, DemoPanel } from "@/components/SalesManager";
import { PlatformUsers } from "@/components/PlatformUsers";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const LANGS = [{ value: "en", label: "English" }, { value: "es", label: "Spanish" }];
const PLANS = ["Pilot", "Starter", "Growth", "Agency Pro", "Enterprise"];
const BILLING = ["trial", "active", "past_due", "paused", "cancelled"];
const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "America/Puerto_Rico"];
const EMPTY = {
  legal_name: "", display_name: "", contact_email: "", contact_phone: "",
  default_language: "en", timezone: "America/New_York", plan: "", billing_status: "",
  active: true, admin_name: "", admin_email: "",
};

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-black/60 p-3 sm:p-4" data-testid={testid}>
      <div className="my-auto max-h-[calc(100dvh-1.5rem)] w-full min-w-0 max-w-lg space-y-4 overflow-y-auto rounded-xl border bg-card p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="font-semibold">{title}</div>
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ActivationLink({ link, status, onClose }) {
  const { t } = useTranslation();
  const pending = status && status.status !== "sent";
  return (
    <Modal title={t("platform.activation.title")} onClose={onClose} testid="activation-modal">
      <div className="space-y-3 text-sm">
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800" data-testid="email-provider-status">
          {pending ? t("platform.activation.pending") : t("platform.activation.sent")}
        </div>
        <div className="flex items-center gap-2">
          <Input readOnly value={link} data-testid="activation-link-input" className="text-xs" />
          <Button size="sm" variant="outline" data-testid="activation-link-copy"
            onClick={() => { navigator.clipboard?.writeText(link).then(() => toast.success(t("platform.activation.copied"))).catch(() => {}); }}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("platform.activation.note")}</p>
        <div className="flex justify-end"><Button size="sm" onClick={onClose} data-testid="activation-modal-done">{t("platform.activation.done")}</Button></div>
      </div>
    </Modal>
  );
}

function AgencyForm({ initial, isEdit, onSubmit, onClose, busy }) {
  const { t } = useTranslation();
  const [f, setF] = useState(initial || EMPTY);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const field = (k, label, type = "text", testid) => (
    <div><Label className="text-xs">{label}</Label>
      <Input type={type} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={testid} /></div>
  );
  return (
    <Modal title={isEdit ? t("platform.form.edit_title") : t("platform.form.new_title")} onClose={onClose} testid="agency-form-modal">
      <div className="grid max-h-[60dvh] grid-cols-1 gap-3 overflow-y-auto text-sm sm:grid-cols-2">
        {field("legal_name", t("platform.form.legal_name"), "text", "agency-legal-name")}
        {field("display_name", t("platform.form.display_name"), "text", "agency-display-name")}
        {field("contact_email", t("platform.form.contact_email"), "email", "agency-contact-email")}
        {field("contact_phone", t("platform.form.contact_phone"), "text", "agency-contact-phone")}
        <div><Label className="text-xs">{t("platform.form.default_language")}</Label>
          <Select value={f.default_language} onValueChange={(v) => set("default_language", v)}>
            <SelectTrigger data-testid="agency-language"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.value === "es" ? t("common.spanish") : t("common.english")}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">{t("platform.form.timezone")}</Label><select value={f.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="agency-timezone" className="h-10 w-full rounded-md border bg-background px-3 text-sm">{TIMEZONES.map((z) => <option key={z}>{z}</option>)}</select></div>
        <div><Label className="text-xs">{t("platform.form.plan")}</Label><select value={f.plan} onChange={(e) => set("plan", e.target.value)} data-testid="agency-plan" className="h-10 w-full rounded-md border bg-background px-3 text-sm"><option value="">Select plan</option>{PLANS.map((p) => <option key={p}>{p}</option>)}</select></div>
        <div><Label className="text-xs">{t("platform.form.billing_status")}</Label><select value={f.billing_status} onChange={(e) => set("billing_status", e.target.value)} data-testid="agency-billing-status" className="h-10 w-full rounded-md border bg-background px-3 text-sm"><option value="">Select status</option>{BILLING.map((p) => <option key={p}>{p}</option>)}</select></div>
        {!isEdit && field("admin_name", t("platform.form.admin_name"), "text", "agency-admin-name")}
        {!isEdit && field("admin_email", t("platform.form.admin_email"), "email", "agency-admin-email")}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        <Button size="sm" onClick={() => onSubmit(f)} disabled={busy} data-testid="agency-form-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? t("platform.form.save") : t("platform.form.create"))}
        </Button>
      </div>
    </Modal>
  );
}

function AssignAdminForm({ agency, onSubmit, onClose, busy }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  return (
    <Modal title={t("platform.assign.title", { name: agency.display_name || agency.name })} onClose={onClose} testid="assign-admin-modal">
      <div className="space-y-3 text-sm">
        <div><Label className="text-xs">{t("platform.assign.admin_name")}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="assign-admin-name" /></div>
        <div><Label className="text-xs">{t("platform.assign.admin_email")}</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="assign-admin-email" /></div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button size="sm" onClick={() => onSubmit({ name, email })} disabled={busy} data-testid="assign-admin-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("platform.assign.send")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function Platform() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("agencies");
  const [agencies, setAgencies] = useState([]);
  const [onboarding, setOnboarding] = useState([]);
  const [emailData, setEmailData] = useState(null);
  const [form, setForm] = useState(null);       // {mode:'create'|'edit', agency}
  const [assign, setAssign] = useState(null);    // agency
  const [activation, setActivation] = useState(null); // {link,status}
  const [usersAgency, setUsersAgency] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAgencies = () => api.get("/platform/agencies").then(({ data }) => setAgencies(data)).catch(() => {});
  const loadOnboarding = () => api.get("/platform/onboarding").then(({ data }) => setOnboarding(data)).catch(() => {});
  const loadEmail = () => api.get("/platform/email-status").then(({ data }) => setEmailData(data)).catch(() => {});
  useEffect(() => { loadAgencies(); loadOnboarding(); loadEmail(); }, []);

  const submitAgency = async (f) => {
    setBusy(true);
    try {
      if (form.mode === "edit") {
        await api.put(`/platform/agencies/${form.agency.id}`, f);
        toast.success(t("platform.toasts.agency_updated"));
      } else {
        const { data } = await api.post("/platform/agencies", f);
        toast.success(t("platform.toasts.agency_created"));
        if (data.invite_link) setActivation({ link: data.invite_link, status: data.email_status });
      }
      setForm(null); loadAgencies(); loadOnboarding();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("platform.toasts.failed")); }
    finally { setBusy(false); }
  };

  const submitAssign = async ({ name, email }) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/agencies/${assign.id}/assign-admin`, { name, email });
      toast.success(t("platform.toasts.admin_invited"));
      setAssign(null);
      if (data.invite_link) setActivation({ link: data.invite_link, status: data.email_status });
      loadAgencies(); loadOnboarding();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("platform.toasts.failed")); }
    finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.post(`/platform/agencies/${a.id}/active`, { active: !(a.active === true) });
      toast.success(a.active ? t("platform.toasts.deactivated") : t("platform.toasts.activated"));
      loadAgencies(); loadOnboarding();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("platform.toasts.failed")); }
  };

  const viewAgency = async (a) => {
    try { await api.post(`/platform/impersonation/start/${a.id}`); window.location.assign("/"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const unassignAdmin = async (a) => {
    if (!window.confirm(`Remove the primary admin assignment from ${a.display_name || a.name}? The user account will remain available in Users.`)) return;
    try { await api.delete(`/platform/agencies/${a.id}/admin`); toast.success("Primary admin unassigned"); loadAgencies(); loadOnboarding(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const TABS = [["agencies", t("platform.tabs.agencies"), Building2], ["users", "Users & Access", Users], ["prospects", t("platform.tabs.prospects"), Contact2], ["demo", t("platform.tabs.demo"), Beaker], ["billing", t("platform.tabs.billing"), DollarSign], ["onboarding", t("platform.tabs.onboarding"), ListChecks], ["email", t("platform.tabs.email"), Mail]];

  return (
    <div className="space-y-5" data-testid="platform-console">
      <PageHeader title={t("platform.title")} subtitle={t("platform.subtitle")} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-b">
        <div className="flex flex-wrap gap-2">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`platform-tab-${k}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${tab === k ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        {tab === "agencies" && (
          <Button size="sm" data-testid="new-agency-btn" onClick={() => setForm({ mode: "create", agency: null })}>{t("platform.new_agency")}</Button>
        )}
      </div>

      {tab === "agencies" && (
        <div className="rounded-lg border bg-card p-3 sm:p-4" data-testid="platform-agencies">
          <div className="w-full overflow-x-auto" role="region" aria-label="Agency directory" tabIndex={0}>
          <table className="min-w-[760px] w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2">{t("platform.table.agency")}</th><th>{t("platform.table.plan")}</th><th>{t("platform.table.billing")}</th><th>{t("platform.table.users")}</th><th>{t("platform.table.onboarding")}</th><th>{t("platform.table.status")}</th><th>{t("platform.table.actions")}</th></tr></thead>
            <tbody>
              {agencies.map((a) => (
                <tr key={a.id} className="border-b" data-testid={`agency-row-${a.id}`}>
                  <td className="py-2">{a.display_name || a.name}
                    <div className="text-xs text-muted-foreground">{a.contact_email || "—"}</div></td>
                  <td>{a.plan || "—"}</td>
                  <td>{a.billing_status || "—"}</td>
                  <td className="tabular-nums"><button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => { setUsersAgency(a.id); setTab("users"); }}><Users className="h-3.5 w-3.5" />{a.user_count}</button></td>
                  <td className="tabular-nums" data-testid={`agency-progress-${a.id}`}>{a.onboarding_progress}%</td>
                  <td>{a.active === true
                    ? <span className="text-emerald-600" data-testid={`agency-status-${a.id}`}>{t("platform.status.active")}</span>
                    : <span className="text-red-600" data-testid={`agency-status-${a.id}`}>{t("platform.status.inactive")}</span>}</td>
                  <td className="space-x-1 py-1">
                    <Button size="sm" variant="outline" data-testid={`agency-edit-${a.id}`} onClick={() => setForm({ mode: "edit", agency: a })}>{t("platform.actions.edit")}</Button>
                    <Button size="sm" variant="outline" data-testid={`agency-assign-${a.id}`} onClick={() => setAssign(a)}>{t("platform.actions.assign_admin")}</Button>
                    {a.primary_admin_id && <Button size="sm" variant="outline" onClick={() => unassignAdmin(a)}>Remove admin</Button>}
                    <Button size="sm" variant="outline" onClick={() => viewAgency(a)}>View as agency</Button>
                    <Button size="sm" variant="outline" data-testid={`agency-toggle-${a.id}`} onClick={() => toggleActive(a)}>{a.active === true ? t("platform.actions.deactivate") : t("platform.actions.activate")}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "users" && <PlatformUsers key={usersAgency || "all"} agencies={agencies} initialAgencyId={usersAgency}
        onActivation={(data) => setActivation({ link: data.invite_link, status: data.email_status })} />}

      {tab === "billing" && <PlatformBilling agencies={agencies} />}

      {tab === "prospects" && <ProspectsPanel />}

      {tab === "demo" && <DemoPanel />}

      {tab === "onboarding" && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="platform-onboarding">
          {onboarding.map((o) => (
            <div key={o.agency_id} className="rounded-lg border bg-card p-4" data-testid={`onboarding-card-${o.agency_id}`}>
              <div className="mb-3 flex items-center justify-between">
                <div className="font-semibold">{o.name}</div>
                <span className={`text-xs ${o.active ? "text-emerald-600" : "text-red-600"}`}>{o.active ? t("platform.status.active") : t("platform.status.inactive")}</span>
              </div>
              <OnboardingChecklist data={o.onboarding} testid={`onboarding-${o.agency_id}`} />
            </div>
          ))}
        </div>
      )}

      {tab === "email" && emailData && (
        <div className="space-y-3 rounded-lg border bg-card p-4" data-testid="platform-email">
          <div className="w-full overflow-x-auto" role="region" aria-label="Platform email delivery log" tabIndex={0}>
          <div className={`rounded-md px-3 py-2 text-sm ${emailData.provider.configured ? "bg-emerald-50 text-emerald-800" : "border border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="platform-email-provider">
            {emailData.provider.configured
              ? t("platform.email.configured", { email: emailData.provider.from_email })
              : t("platform.email.not_configured", { missing: emailData.provider.missing.join(", ") })}
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2">{t("platform.email.col_time")}</th><th>{t("platform.email.col_recipient")}</th><th>{t("platform.email.col_type")}</th><th>{t("platform.email.col_status")}</th><th>{t("platform.email.col_provider")}</th></tr></thead>
            <tbody>
              {emailData.messages.map((m) => (
                <tr key={m.id} className="border-b text-xs">
                  <td className="py-1.5">{m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</td>
                  <td>{m.recipient}</td><td>{m.kind}</td>
                  <td>{m.status === "sent" ? <span className="text-emerald-600">{t("platform.email.sent")}</span>
                    : <span className="text-amber-600">{m.status}</span>}</td>
                  <td>{m.provider_message_id || "—"}</td>
                </tr>
              ))}
              {emailData.messages.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">{t("platform.email.empty")}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {form && <AgencyForm isEdit={form.mode === "edit"} initial={form.agency ? { ...EMPTY, ...form.agency } : EMPTY}
        onSubmit={submitAgency} onClose={() => setForm(null)} busy={busy} />}
      {assign && <AssignAdminForm agency={assign} onSubmit={submitAssign} onClose={() => setAssign(null)} busy={busy} />}
      {activation && <ActivationLink link={activation.link} status={activation.status} onClose={() => setActivation(null)} />}
    </div>
  );
}
